import React from 'react';
import { Skel } from './Skeleton';
import { marketColor } from './marketColors';
import { BRIEF_BIG_PCT } from '../utils/signalAnalysisCalcs';

/**
 * Signal Analysis — Brief (Apal 2026-10-08): "how we behaved for the selected
 * duration … what worked and what didn't. Keeping it simple … The promise we
 * have is, we try to win big and lose small. Also overall in-market is
 * important … no random text" — then "per market grouping but done in a way
 * that I can see what worked in each market and what didn't". The column
 * columns go by OUR return against a bar that follows the period — 10% a
 * year, compounded (prorated below a year; see briefBar): <bar>%+ · 0–<bar>% ·
 * Lost money; the index rides on every card. Each head says in plain words
 * what its range means ("at least 10% a year, compounded over 2 years") —
 * a separate note row read as detached (Apal: "the good bad text isn't
 * clear").
 *
 *   1. the promise, measured: avg winning trade · avg losing trade · avg win vs
 *      avg loss · time in market
 *   2. a market × outcome grid: one row per market (best average first), three
 *      columns — <bar>%+ · 0–<bar>% · Lost money — each cell best return first
 *
 * The columns are ONE grid's tracks, ruled and tinted top to bottom (Apal: "I
 * want appropriate dividers"; good green, lost red, the middle untinted), so they line up down the page whatever each market
 * holds. Below lg the grid stacks per market, each cell labels itself and
 * empty cells drop out.
 *
 * Numbers are the Detailed tab's own (promiseCheck over computeOverallSummary,
 * computeBriefView over the per-index card stats). No charts, no trade tables.
 */

const signed = (v, digits = 1) => `${v > 0 ? '+' : v < 0 ? '−' : ''}${Math.abs(v).toFixed(digits)}`;
const pctClass = (v) => (v > 0 ? 'text-green-400' : v < 0 ? 'text-red-400' : 'text-dark-300');

/** How the period turns "10% a year" into its bar: nothing to add at one year. */
const barBasis = (bar) => (bar.span === '1 year' ? ''
  : bar.days > 365 ? `, compounded over ${bar.span}` : `, prorated to ${bar.span}`);

export const TIERS = [
  { key: 'big', label: (bar) => `${bar.label}+`, means: (bar) => `at least ${BRIEF_BIG_PCT}% a year${barBasis(bar)}`, tint: 'rgba(34,197,94,0.06)', card: 'bg-emerald-500/10 border-emerald-500/40', head: 'text-emerald-300', rule: 'border-emerald-400' },
  { key: 'small', label: (bar) => `0–${bar.label}`, means: () => `made money, under ${BRIEF_BIG_PCT}% a year`, card: 'bg-dark-700/50 border-dark-500', head: 'text-dark-100', rule: 'border-dark-300' },
  { key: 'lost', label: () => 'Lost money', means: () => 'below 0%', tint: 'rgba(239,68,68,0.06)', card: 'bg-red-500/10 border-red-500/40', head: 'text-red-300', rule: 'border-red-400' },
];

/** Market column + the three outcome columns: shared by the header and every row. */
const ROW_GRID = 'lg:grid lg:grid-cols-[11rem_repeat(3,minmax(0,1fr))]';
/** Cards inside a cell: same tracks in every row of a column, so they line up. */
const CARD_GRID = 'grid gap-2 grid-cols-[repeat(auto-fill,minmax(8.5rem,1fr))]';

const Cell = ({ label, children, testid }) => (
  <div className="bg-dark-800 px-5 py-3.5" data-testid={testid}>
    <div className="text-dark-400 text-[11px] uppercase tracking-widest font-semibold mb-1.5">{label}</div>
    {children}
  </div>
);

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

/**
 * The promise — win big, lose small — measured over the period. Labels name
 * the number exactly (Apal: "not clear what do you mean by Win Big? Lose
 * Small, are these avg winning and avg losing trades … I had to think about
 * it"): average winning trade, average losing trade, how many times bigger
 * the one is than the other, time invested.
 */
const PromiseStrip = ({ promise, inMarket }) => (
  <div data-testid="brief-promise" className="grid grid-cols-2 xl:grid-cols-4 gap-px bg-dark-700 rounded-xl overflow-hidden border border-dark-700 mb-5">
    <Cell label="Avg winning trade" testid="promise-win">
      {promise?.avgWin != null ? (
        <>
          <span className="text-2xl font-bold tabular-nums text-green-400">{signed(promise.avgWin)}%</span>
          <div className="text-xs text-dark-400 tabular-nums mt-0.5">{plural(promise.wins, 'win', 'wins')} · best <span className="text-green-300">{signed(promise.bestWin)}%</span> {promise.bestWinLabel}</div>
        </>
      ) : <span className="text-2xl font-bold text-dark-500">—</span>}
    </Cell>
    <Cell label="Avg losing trade" testid="promise-loss">
      {promise?.avgLoss != null ? (
        <>
          <span className="text-2xl font-bold tabular-nums text-red-400">{signed(promise.avgLoss)}%</span>
          <div className="text-xs text-dark-400 tabular-nums mt-0.5">{plural(promise.losses, 'loss', 'losses')} · worst <span className="text-red-300">{signed(promise.worstLoss)}%</span> {promise.worstLossLabel}</div>
        </>
      ) : <span className="text-2xl font-bold text-green-400">No losing trades</span>}
    </Cell>
    <Cell label="Avg win vs avg loss" testid="promise-ratio">
      {promise?.ratio != null ? (
        <>
          <span className={`text-2xl font-bold tabular-nums ${promise.held ? 'text-green-400' : 'text-red-400'}`}>{promise.ratio.toFixed(1)}×</span>
          <div className="text-xs text-dark-400 mt-0.5">{promise.held ? `wins are ${promise.ratio.toFixed(1)}× the size of losses` : 'losses are bigger than wins'}</div>
        </>
      ) : <span className="text-2xl font-bold text-dark-500">—</span>}
    </Cell>
    <Cell label="Time in market" testid="promise-in-market">
      {inMarket != null ? (
        <>
          <span className="text-2xl font-bold tabular-nums text-white">{inMarket}%</span>
          <div className="text-xs text-dark-400 mt-0.5">of the period invested · in cash the rest</div>
        </>
      ) : <span className="text-2xl font-bold text-dark-500">—</span>}
    </Cell>
  </div>
);

const IndexCard = ({ c, tier, onOpen }) => (
  <button type="button" data-testid="brief-card" data-symbol={c.symbol} data-tier={c.tier}
    onClick={() => onOpen && onOpen(c.symbol)}
    title={`${c.label}: ours ${signed(c.ours, 2)}% vs index ${signed(c.index, 2)}%. Click for the full card.`}
    className={`min-w-0 text-left rounded-lg border px-2.5 py-2 transition hover:brightness-125 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400 ${tier.card}`}>
    <div className="text-[13px] font-semibold text-dark-100 leading-snug break-words">{c.label}</div>
    <div className={`text-xl font-bold tabular-nums leading-tight ${pctClass(c.ours)}`} data-stat="ours">{signed(c.ours)}%</div>
    <div className="text-[11px] text-dark-400 tabular-nums">index <span className="text-sky-300">{signed(c.index)}%</span></div>
  </button>
);

/** Left column: which market, how it did on average, how many beat their index. */
const MarketHead = ({ m }) => (
  <div className="pb-2 lg:p-3 lg:pl-4 bg-dark-800/40" data-testid="brief-market-head">
    <div className="flex items-center gap-2 text-sm font-bold uppercase tracking-wider" style={{ color: marketColor(m.market) }}>
      <span className="w-2 h-2 rounded-full shrink-0" style={{ background: marketColor(m.market) }} />{m.name}
    </div>
    <div className="mt-1.5 grid grid-cols-[auto_1fr] gap-x-3 text-xs tabular-nums">
      <span className="text-dark-400">avg</span>
      <span className={`font-semibold ${pctClass(m.ours)}`} data-stat="market-ours">{signed(m.ours)}%</span>
      <span className="text-dark-400">index</span>
      <span className="text-sky-300" data-stat="market-index">{signed(m.index)}%</span>
    </div>
    <div className="mt-1 text-xs text-dark-400 tabular-nums" data-stat="market-beat">{m.beat} of {m.count} beat the index</div>
  </div>
);

/**
 * The Brief while its numbers load (Apal 2026-11-03: "appropriate skeleton for
 * each page to create illusion of exactly what's gonna load"): the four promise
 * cells with their real labels, then the market grid — one row per selected
 * market, its name in its colour, card shapes in the three columns.
 */
export const BriefSkeleton = ({ markets }) => {
  const rows = markets && markets.length ? markets : [{ market: '', name: '' }, { market: '', name: '' }, { market: '', name: '' }];
  return (
    <div data-testid="signal-brief-loading" aria-hidden="true">
      <div className="grid grid-cols-2 xl:grid-cols-4 gap-px bg-dark-700 rounded-xl overflow-hidden border border-dark-700 mb-5">
        {['Avg winning trade', 'Avg losing trade', 'Avg win vs avg loss', 'Time in market'].map(label => (
          <Cell key={label} label={label}>
            <div className="h-8 flex items-center"><Skel className="h-6 w-20" /></div>
            <div className="h-4 flex items-center mt-0.5"><Skel className="h-2.5 w-40" /></div>
          </Cell>
        ))}
      </div>
      <div className="relative rounded-xl border border-dark-700 overflow-hidden">
        <div className={`hidden ${ROW_GRID} bg-dark-800 border-b-2 border-dark-600`}>
          <div className="px-4 py-2.5 text-[11px] uppercase tracking-widest font-semibold text-dark-400 self-center">Market</div>
          {TIERS.map(t => (
            <div key={t.key} className={`border-t-2 ${t.rule} px-4 py-2.5`}>
              <div className="h-5 flex items-center"><Skel className="h-3 w-16" /></div>
              <div className="h-4 flex items-center mt-0.5"><Skel className="h-2.5 w-40" /></div>
            </div>
          ))}
        </div>
        {rows.map((m, i) => (
          <section key={m.market || i} data-skel-market={m.market} className={`${ROW_GRID} border-b border-dark-600 last:border-b-0 px-4 py-3 lg:p-0`}>
            <div className="pb-2 lg:p-3 lg:pl-4 bg-dark-800/40">
              {m.name ? (
                <div className="flex items-center gap-2 text-sm font-bold uppercase tracking-wider" style={{ color: marketColor(m.market) }}>
                  <span className="w-2 h-2 rounded-full shrink-0" style={{ background: marketColor(m.market) }} />{m.name}
                </div>
              ) : <Skel className="h-3.5 w-24" />}
              <div className="mt-2 space-y-1.5"><Skel className="h-2.5 w-20 block" /><Skel className="h-2.5 w-16 block" /><Skel className="h-2.5 w-28 block" /></div>
            </div>
            {TIERS.map((t, j) => (
              <div key={t.key} className="min-w-0 py-1 lg:p-3">
                <div className={CARD_GRID}>
                  {Array.from({ length: (i + j) % 2 ? 1 : 2 }, (_, k) => <Skel key={k} className="h-[68px] w-full block" rounded="rounded-lg" />)}
                </div>
              </div>
            ))}
          </section>
        ))}
        <div aria-hidden="true" className={`hidden ${ROW_GRID} absolute inset-0 pointer-events-none`}>
          <div />
          {TIERS.map(t => <div key={t.key} className="border-l border-dark-600" style={t.tint ? { background: t.tint } : undefined} />)}
        </div>
      </div>
    </div>
  );
};

const SignalBrief = ({ brief, promise, inMarket, onOpen, markets }) => {
  // prices still loading: the Brief's own shape, not a false "no trades"
  if (!brief) return <BriefSkeleton markets={markets} />;
  if (!brief.total) {
    return <div className="text-dark-400 text-sm py-10 text-center" data-testid="signal-brief">No index has a trade in this period.</div>;
  }
  return (
    <div data-testid="signal-brief">
      <PromiseStrip promise={promise} inMarket={inMarket} />
      <div className="relative rounded-xl border border-dark-700 overflow-hidden">
        {/* Column heads, once — lg and up (below that each cell labels itself) */}
        <div className={`hidden ${ROW_GRID} bg-dark-800 border-b-2 border-dark-600`} data-testid="brief-col-heads">
          <div className="px-4 py-2.5 text-[11px] uppercase tracking-widest font-semibold text-dark-400 self-center">Market</div>
          {TIERS.map(t => (
            <div key={t.key} data-col-head={t.key} className={`border-t-2 ${t.rule} px-4 py-2.5`}>
              <div className="flex items-baseline justify-between gap-3">
                <span className={`text-sm font-bold uppercase tracking-wider ${t.head}`} data-col-title>{t.label(brief.bar)}</span>
                <span className={`text-xs font-semibold tabular-nums whitespace-nowrap ${t.head}`} data-col-count>{brief.counts[t.key]} {brief.counts[t.key] === 1 ? 'index' : 'indexes'}</span>
              </div>
              <div className="text-xs text-dark-300 mt-0.5" data-col-means>{t.means(brief.bar)}</div>
            </div>
          ))}
        </div>
        {brief.markets.map(m => (
          <section key={m.market} data-market-row={m.market}
            className={`${ROW_GRID} border-b border-dark-600 last:border-b-0 px-4 py-3 lg:p-0`}>
            <MarketHead m={m} />
            {TIERS.map(t => (
              <div key={t.key} data-cell={t.key} className={`min-w-0 py-1 lg:p-3 ${m.cells[t.key].length ? '' : 'hidden lg:block'}`}>
                <div className="lg:hidden mb-1"><span className={`text-[11px] font-bold uppercase tracking-wider ${t.head}`}>{t.label(brief.bar)}</span> <span className="text-[11px] text-dark-400">{t.means(brief.bar)}</span></div>
                {m.cells[t.key].length ? (
                  <div className={CARD_GRID}>
                    {m.cells[t.key].map(c => <IndexCard key={c.symbol} c={c} tier={t} onOpen={onOpen} />)}
                  </div>
                ) : <div className="text-dark-600 text-sm lg:pt-2" aria-label={`None at ${t.label(brief.bar)} in ${m.name}`}>—</div>}
              </div>
            ))}
          </section>
        ))}
        {/* Column tints and rules, full height — head and every row — drawn
            over the grid (it only tints, never takes a click) */}
        <div aria-hidden="true" data-testid="brief-col-bands" className={`hidden ${ROW_GRID} absolute inset-0 pointer-events-none`}>
          <div />
          {TIERS.map(t => <div key={t.key} data-col-band={t.key} className="border-l border-dark-600" style={t.tint ? { background: t.tint } : undefined} />)}
        </div>
      </div>
    </div>
  );
};

export default SignalBrief;
